import { notFound } from "@tanstack/react-router";
import { getFeaturePageBacklinksQueryOptions } from "@/components/feature-page-backlinks/feature-page-backlinks-query";
import { getDealOfferFormatSummariesQueryOptions } from "@/components/marketplace/deal-offer-format-summaries-query";
import { getDealOfferSummaryQueryOptions, getOutOfStockDealOffersQueryOptions } from "@/components/marketplace/deal-offers-query";
import { getWorkListingsQueryOptions } from "@/components/marketplace/work-listings-query";
import { getSpotifyAlbumQueryOptions } from "@/components/music/spotify-album-query";
import { getWikipediaResourceQueryOptions } from "@/components/wikipedia-resource-query";
import { requireCatalogPageId } from "@/lib/catalog-route-param";
import { WORK_RELEASE_URL_CONFIG, buildFacetedSearchState } from "@/lib/faceted-search-url";
import { getEffectiveWorkReleaseFormat, getWorkReleaseFilter, getWorkReleaseFormatMode } from "@/lib/work-release-format";
import { redirectCatalogRouteIfNeeded } from "@/utils/catalog-route-redirect";
import { resolveLegacyDiscogsCatalogId } from "@/utils/legacy-id-redirect";
import { prefetchForSsr } from "@/utils/loader";
import { releaseSearchFilterToFilter } from "@garimpa/lib/searchState";
import { getWorkQueryOptions } from "./$id/work-query";
import { searchWorkReleasesQueryOptions } from "./$id/work-releases-query";
import { getWorkTrackVideosQueryOptions } from "./$id/work-track-videos-query";
type WorkSearch = {
  q?: string;
  format?: string;
  formatSet?: string[];
  sort?: string;
  country?: string;
  label?: string[];
  company?: string[];
  tag?: string[];
  year?: number[];
  filter?: ReleaseSearchFilter;
  tab?: (typeof VALID_TABS)[number];
  playing?: string;
  page?: number;
};
const SplitLoader = async ({
  context: {
    queryClient,
    locale,
    viewer
  },
  params,
  deps,
  location
}) => {
  const search = location.search as WorkSearch;
  const workId = requireCatalogPageId(params.id);
  const work = await queryClient.ensureQueryData(getWorkQueryOptions(workId, locale));
  if (!work) {
    const legacyCatalogId = await resolveLegacyDiscogsCatalogId({
      entity: "work",
      id: workId,
      queryClient
    });
    if (legacyCatalogId) {
      redirectCatalogRouteIfNeeded({
        force: true,
        location,
        removeSearchParams: ["type", "releaseId"],
        targetPath: `/work/${encodeURIComponent(legacyCatalogId)}`
      });
    }
    throw notFound();
  }
  redirectCatalogRouteIfNeeded({
    location,
    removeSearchParams: ["type", "releaseId"],
    targetPath: `/work/${encodeURIComponent(workId)}`
  });
  const selectedFormatMode = getWorkReleaseFormatMode({
    mode: deps.format
  });
  const state = buildFacetedSearchState(deps, WORK_RELEASE_URL_CONFIG);
  const stateFilter = releaseSearchFilterToFilter(state?.filter, {
    allowed: WORK_RELEASE_URL_CONFIG.keys,
    allTermKeys: WORK_RELEASE_URL_CONFIG.allKeys
  });
  const releaseFilter = getWorkReleaseFilter({
    filter: stateFilter,
    mode: selectedFormatMode,
    mediaCategoryCounts: work.mediaCategoryCounts
  });
  const workReleasesQueryOptions = searchWorkReleasesQueryOptions({
    id: workId,
    filter: releaseFilter,
    sort: deps.sort ?? "date-desc",
    search: search.q,
    page: search.page
  });
  const currency = viewer.preferences.currency;
  const offerFormat = getEffectiveWorkReleaseFormat({
    mode: selectedFormatMode,
    mediaCategoryCounts: work.mediaCategoryCounts
  });
  const prefetches: Promise<unknown>[] = [queryClient.prefetchQuery(workReleasesQueryOptions), queryClient.prefetchQuery(getDealOfferSummaryQueryOptions({
    workId,
    currency,
    format: offerFormat
  }, {
    viewerId: viewer.userId
  })), queryClient.prefetchQuery(getDealOfferFormatSummariesQueryOptions({
    workId,
    currency
  }, {
    viewerId: viewer.userId
  })), queryClient.prefetchQuery(getOutOfStockDealOffersQueryOptions({
    workId,
    currency
  }, {
    viewerId: viewer.userId
  })), queryClient.prefetchQuery(getWorkListingsQueryOptions({
    workId,
    currency
  })), queryClient.prefetchQuery(getWorkTrackVideosQueryOptions(workId)), queryClient.prefetchQuery(getSpotifyAlbumQueryOptions({
    workId
  })), queryClient.prefetchQuery(getFeaturePageBacklinksQueryOptions({
    entityType: "work",
    catalogId: workId,
    locale
  }))];
  if (work.wikidataId) {
    prefetches.push(queryClient.prefetchQuery(getWikipediaResourceQueryOptions({
      wikidataId: work.wikidataId,
      locale
    })));
  }
  await prefetchForSsr(prefetches);
  return {
    work
  };
};
export { SplitLoader as loader };